import React, { useEffect, useState } from "react";
import api, { apiErr, uploadFile, fileUrl } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [details, setDetails] = useState([]);
  const [checked, setChecked] = useState({});
  const [confirm, setConfirm] = useState(false);

  useEffect(() => { api.get("/payroll/periods").then((r) => setPeriods(r.data)); }, []);
  const load = (pid) => api.get(`/payroll/periods/${pid}/details`).then((r) => { setDetails(r.data); setChecked({}); });
  useEffect(() => { if (periodId) load(periodId); }, [periodId]);

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const selectedIds = Object.keys(checked).filter((k) => checked[k]);
  const toPay = details.filter((d) => selectedIds.includes(d.id) && d.status_pembayaran !== "SUDAH DIBAYAR");
  const totalSel = toPay.reduce((s, d) => s + Number(d.gaji_bersih || 0), 0);

  const markPaid = async () => {
    try {
      await api.post("/payroll/payments/mark-paid", { detail_ids: toPay.map((d) => d.id), metode_pembayaran: "Transfer Bank" });
      toast.success(`${toPay.length} pegawai ditandai sudah dibayar`); setConfirm(false); load(periodId);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const uploadBukti = async (e, id) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await uploadFile(f, "bukti");
      await api.put(`/payroll/details/${id}/bukti`, { path: r.path });
      toast.success("Bukti transfer terunggah"); load(periodId);
    } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };
  const removeBukti = async (id) => {
    try { await api.put(`/payroll/details/${id}/bukti`, { path: "" }); toast.success("Bukti dihapus"); load(periodId); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const allUnpaid = details.filter((d) => d.status_pembayaran !== "SUDAH DIBAYAR");

  return (
    <div className="space-y-6">
      <PageHeader title="Pembayaran & Transfer" subtitle="Tandai gaji yang sudah dibayarkan" />
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="max-w-md flex-1">
            <F label="Pilih Periode">
              <Select value={periodId} onValueChange={setPeriodId}>
                <SelectTrigger data-testid="pay-period-select"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
                <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama} — {p.status}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          {allUnpaid.length > 0 && <Button variant="outline" onClick={() => setChecked(Object.fromEntries(allUnpaid.map((d) => [d.id, true])))} data-testid="pay-select-all">Pilih Semua Belum Dibayar</Button>}
        </div>
      </Card>

      {periodId && (
        <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="pay-table">
            <thead><tr className="border-b bg-slate-100/80 text-xs font-bold uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2.5 w-10"></th>
              <th className="px-3 py-2.5 text-left">Nama</th>
              <th className="px-3 py-2.5 text-left">Bank</th>
              <th className="px-3 py-2.5 text-left">No. Rekening</th>
              <th className="px-3 py-2.5 text-right">Total Gaji</th>
              <th className="px-3 py-2.5 text-left">Status</th>
              <th className="px-3 py-2.5 text-left">Tgl Bayar</th>
              <th className="px-3 py-2.5 text-left">Bukti</th>
            </tr></thead>
            <tbody>
              {details.length === 0 ? <tr><td colSpan={7} className="text-center text-slate-400 py-8">Belum ada data. Pastikan payroll sudah dihitung.</td></tr> :
                details.map((d, i) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pay-row-${i}`}>
                    <td className="px-3 py-2">{d.status_pembayaran !== "SUDAH DIBAYAR" && <Checkbox checked={!!checked[d.id]} onCheckedChange={() => toggle(d.id)} data-testid={`pay-check-${i}`} />}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{d.employee_nama}</td>
                    <td className="px-3 py-2">{d.nama_bank || "-"}</td>
                    <td className="px-3 py-2 font-mono">{d.no_rekening || "-"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{rupiah(d.gaji_bersih)}</td>
                    <td className="px-3 py-2"><StatusBadge status={d.status_pembayaran} /></td>
                    <td className="px-3 py-2 text-slate-500">{d.tanggal_pembayaran ? formatDate(d.tanggal_pembayaran) : "-"}</td>
                    <td className="px-3 py-2">
                      {d.bukti_transfer
                        ? <span className="flex items-center gap-2">
                            <a href={fileUrl(d.bukti_transfer)} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline">Lihat</a>
                            <label className="text-xs text-slate-600 underline cursor-pointer"><input type="file" accept="image/*,.pdf" className="hidden" data-testid={`pay-bukti-ganti-${i}`} onChange={(e) => uploadBukti(e, d.id)} />Ganti</label>
                            <button className="text-xs text-rose-600" data-testid={`pay-bukti-del-${i}`} onClick={() => removeBukti(d.id)}>Hapus</button>
                          </span>
                        : <label className="text-xs text-blue-700 underline cursor-pointer"><input type="file" accept="image/*,.pdf" className="hidden" data-testid={`pay-bukti-${i}`} onChange={(e) => uploadBukti(e, d.id)} />Upload</label>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {toPay.length > 0 && (
        <div className="sticky bottom-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
          <div className="text-sm">{toPay.length} pegawai · Total <span className="font-mono font-bold">{rupiah(totalSel)}</span></div>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setConfirm(true)} data-testid="pay-mark-btn"><CreditCard className="h-4 w-4 mr-1" /> Tandai Sudah Dibayar</Button>
        </div>
      )}

      <ConfirmDialog open={confirm} onOpenChange={setConfirm} testid="pay-confirm-dialog"
        title="Konfirmasi Pembayaran" confirmText="Ya, Tandai Dibayar"
        description={`Tandai ${toPay.length} pegawai (total ${rupiah(totalSel)}) sebagai SUDAH DIBAYAR?`}
        onConfirm={markPaid} />
    </div>
  );
}
